"use client"

/**
 * AiLoginGate — login wall for interactive AI features on the apps that are
 * browsable without logging in (shop, stream, news, …).
 *
 * Product decision (2026-06): on public apps the AI surfaces must still SHOW
 * (not be hidden) for logged-out visitors, but be unusable — rendered blurred
 * with a "Log in to use askFinz AI" card over them. The card links to
 * login.askfinz.ai/auth/login and /auth/signup (with a redirect back).
 *
 * IMPORTANT — this is UI only. It is NOT the security boundary. A visitor who
 * deletes the overlay in devtools gains nothing: the interactive AI endpoints
 * (/api/chat, /api/agent/run, /api/stream/ai/ask, /ai/dj-intro, …) all require
 * auth server-side and return 401 to anonymous callers. This component only
 * decides whether to draw the wall; the backend decides whether AI actually
 * runs. Keep both in sync — never expose an interactive AI endpoint publicly.
 */

import React, { useEffect, useState } from "react"

const SESSION_MARKER_COOKIE = "AskFinz_session_marker"
const AUTH_COOKIE = "AskFinz_auth_token"

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1")
  const m = document.cookie.match(new RegExp("(?:^|; )" + escaped + "=([^;]*)"))
  return m ? decodeURIComponent(m[1]) : null
}

/**
 * Hydration-safe "is the user logged in?" for UI gating only.
 *
 * Starts OPTIMISTICALLY `true` on the server and the first client render — so
 * SSR markup matches the first client render (no React #418) AND logged-in
 * users (the majority, since most apps are fully login-walled) never see a
 * wall flash. An effect then resolves the real state from the shared
 * cross-subdomain session marker; for an anonymous visitor the gate snaps
 * shut a frame later. That brief unwalled frame is harmless: the interactive
 * AI endpoints reject anonymous callers server-side regardless.
 */
export function useIsAuthedClient(): boolean {
  const [authed, setAuthed] = useState(true)
  useEffect(() => {
    const check = () => setAuthed(!!(readCookie(SESSION_MARKER_COOKIE) || readCookie(AUTH_COOKIE)))
    check()
    const onStorage = (e: StorageEvent) => { if (!e.key || e.key.indexOf("AskFinz") !== -1) check() }
    window.addEventListener("authUserUpdated", check)
    window.addEventListener("focus", check)
    window.addEventListener("storage", onStorage)
    return () => {
      window.removeEventListener("authUserUpdated", check)
      window.removeEventListener("focus", check)
      window.removeEventListener("storage", onStorage)
    }
  }, [])
  return authed
}

/** Build the login / signup URLs, carrying a redirect back to the current page. */
export function getAuthUrls(): { login: string; signup: string } {
  const base = (typeof process !== "undefined" && process.env?.NEXT_PUBLIC_LOGIN_APP_URL)
    ? process.env.NEXT_PUBLIC_LOGIN_APP_URL.replace(/\/$/, "")
    : "https://login.askfinz.ai"
  let redirect = ""
  try {
    if (typeof window !== "undefined") redirect = "?redirect=" + encodeURIComponent(window.location.href)
  } catch { /* ignore */ }
  return { login: `${base}/auth/login${redirect}`, signup: `${base}/auth/signup${redirect}` }
}

function go(kind: "login" | "signup") {
  const urls = getAuthUrls()
  if (typeof window !== "undefined") window.location.href = urls[kind]
}

const GATE_STYLE = `
.afn-ailg-wrap { position: relative; }
.afn-ailg-blur { filter: blur(4px); pointer-events: none; user-select: none; }
.afn-ailg-overlay {
  position: absolute; inset: 0; z-index: 50;
  display: flex; align-items: center; justify-content: center;
  padding: 16px; border-radius: inherit;
  background: rgba(8,8,10,0.42);
  -webkit-backdrop-filter: saturate(120%) blur(1px); backdrop-filter: saturate(120%) blur(1px);
}
.afn-ailg-card {
  width: 100%; max-width: 320px; text-align: center;
  padding: 20px 18px; border-radius: 14px;
  background: #ffffff; color: #0b0b0c; border: 1px solid rgba(0,0,0,0.08);
  box-shadow: 0 18px 50px rgba(0,0,0,0.35);
}
.dark .afn-ailg-card { background: #161618; color: #f5f5f6; border-color: rgba(255,255,255,0.10); }
.afn-ailg-icon {
  width: 38px; height: 38px; margin: 0 auto 10px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(0,0,0,0.06);
}
.dark .afn-ailg-icon { background: rgba(255,255,255,0.08); }
.afn-ailg-title { font-size: 14px; font-weight: 700; margin: 0 0 4px; }
.afn-ailg-sub { font-size: 12px; line-height: 1.5; opacity: 0.7; margin: 0 0 14px; }
.afn-ailg-btns { display: flex; gap: 8px; }
.afn-ailg-btn {
  flex: 1; height: 34px; border-radius: 9px; font-size: 12.5px; font-weight: 600;
  cursor: pointer; border: 1px solid transparent; transition: opacity .15s, background .15s;
}
.afn-ailg-btn:hover { opacity: 0.9; }
.afn-ailg-login { background: #111113; color: #fff; }
.dark .afn-ailg-login { background: #fafafa; color: #111113; }
.afn-ailg-signup { background: transparent; color: inherit; border-color: rgba(0,0,0,0.15); }
.dark .afn-ailg-signup { border-color: rgba(255,255,255,0.18); }
`

function LockIcon() {
  return (
    <svg className="afn-ailg-icon" viewBox="0 0 24 24" width="38" height="38" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  )
}

/** The login card + backdrop. Position it inside a `position: relative` parent
 * (it fills the parent via inset:0). Use directly when you can't wrap children. */
export function AiLoginOverlay({
  title = "Log in to use askFinz AI",
  subtitle = "Sign in to chat, ask and generate with askFinz.",
}: { title?: string; subtitle?: string }) {
  return (
    <div className="afn-ailg-overlay" role="dialog" aria-label={title}>
      <style>{GATE_STYLE}</style>
      <div className="afn-ailg-card">
        <LockIcon />
        <p className="afn-ailg-title">{title}</p>
        <p className="afn-ailg-sub">{subtitle}</p>
        <div className="afn-ailg-btns">
          <button type="button" className="afn-ailg-btn afn-ailg-login" onClick={() => go("login")}>Log in</button>
          <button type="button" className="afn-ailg-btn afn-ailg-signup" onClick={() => go("signup")}>Sign up</button>
        </div>
      </div>
    </div>
  )
}

/**
 * Wrap an interactive AI component. When the visitor is logged out the children
 * render blurred + non-interactive with the login card over them; when logged
 * in the children render untouched.
 *
 * Pass `active` to override the auth check (e.g. force the wall regardless), or
 * leave it undefined to use the shared session marker.
 */
export function AiLoginGate({
  children,
  title,
  subtitle,
  active,
  minHeight,
  className,
}: {
  children: React.ReactNode
  title?: string
  subtitle?: string
  active?: boolean
  minHeight?: number | string
  className?: string
}) {
  const authed = useIsAuthedClient()
  const locked = active !== undefined ? active : !authed
  if (!locked) return <>{children}</>
  return (
    <div className={`afn-ailg-wrap ${className || ""}`} style={minHeight ? { minHeight } : undefined}>
      <div className="afn-ailg-blur" aria-hidden>{children}</div>
      <AiLoginOverlay title={title} subtitle={subtitle} />
    </div>
  )
}
